/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import AppliedFilterBar from '@ion/dls/src/filters/AppliedFilterBar';
import {AppliedFilterBarItem} from '@ion/dls/src/filters/AppliedFilterBarTypes';
import {DLSTheme} from '@ion/dls/src/types/types';
import {QueryChipData} from './types';

export interface DisplayQueryChipsLayoutProps {
  queries: QueryChipData[];
  onClearAll: () => void;
  onQueryRemove: (attribute: string) => () => void;
  onClickQueryChip: (queryChipData: QueryChipData) => void;
};

// AppliedFilterBar's own root is `position: sticky` with a `marginBottom`
// equal to its own height, tuned for Tracking's layout - there, the
// negative margin pulls the map (meant to sit behind it) up underneath it.
// Rendered here in Modular Search's own normal document flow, that same
// negative margin instead pulled the search widget/grid controls/grid
// column headers below it up underneath the (opaque) bar, hiding them.
// This wrapper restores the original absolute-positioned overlay (float
// above the page at a fixed negative offset, out of flow entirely) so nothing
// below it shifts position - matching how this chip bar rendered before it
// was rebuilt on AppliedFilterBar ...
const useStyles = makeStyles((theme: DLSTheme) => ({
  overlay : {
    position                       : 'absolute',
    width                          : '100%',
    marginTop                      : '-48px',
    // this bar only reaches mobile when a caller sets
    // isMobileModularSearch=true (ctc-admin's modular search path) - there's
    // no verified mobile-specific offset for this overlay yet, so let it
    // flow in normal position there instead of guessing a magic negative
    // offset that happens to line up ...
    [theme.breakpoints.down('sm')] : {
      position  : 'static',
      marginTop : 0
    }
  }
}));

/**
 *
 * import {DisplayQueryChipsLayout} from '@ion/features/src/search'
 *
 */
export const DisplayQueryChipsLayout = ({
  queries,
  onClearAll,
  onQueryRemove,
  onClickQueryChip
}: DisplayQueryChipsLayoutProps) => {
  const classes = useStyles();

  // reuses the same applied-filter chip bar as Tracking (fixed one-line row,
  // "{count} more" overflow flyout, no grouping needed here) - onClick is
  // set apart from onDelete so clicking a chip still repopulates the search
  // box for editing, only the X icon removes it. groupLabel is set to the
  // same empty string for every item (rather than left undefined) so the
  // overflow modal's grouping - which defaults an ungrouped item to its own
  // id, i.e. its own group - puts every chip in ONE shared group instead of
  // one group per chip; an empty label still renders no group heading ...
  const items: AppliedFilterBarItem[] = useMemo(() => queries.map(query => {
    function onChipClick() {
      onClickQueryChip(query);
    }
    return {
      id         : query.attribute,
      label      : query.label,
      groupLabel : '',
      onClick    : onChipClick,
      onDelete   : onQueryRemove(query.attribute)
    };
  }), [queries, onClickQueryChip, onQueryRemove]);

  return (
    <div className={classes.overlay}>
      <AppliedFilterBar count={queries.length}
        items={items}
        isModularSearch
        onClearAll={onClearAll}
        hideLabel
        skipClearAllConfirmation />
    </div>
  );
};

export default React.memo(DisplayQueryChipsLayout);
